/**
 * Developer overlay. ARCHITECTURE.md §4 (required display list) and §11.1 (pooled debug primitives).
 *
 * Draws the ACTUAL gameplay collision volumes straight from `src/physics` — never a re-derived
 * copy. That is the whole point: if the overlay and the simulation could disagree, the overlay
 * would be worthless for diagnosing hit detection.
 *
 * Every primitive comes from a fixed pool. Nothing is allocated per frame or per hit.
 */

import * as THREE from 'three';
import { DisposalBag, System, toF60, clamp } from '../core/index.js';
import { HURTBOX_COUNT } from '../physics/index.js';

/** Hard cap on simultaneously drawn volumes: 2 x (3 hurt + 1 push + 8 hit + 1 throw) = 26. */
const VOLUME_SLOTS = 28;

const COLOURS = {
  hurt: 0x35c86a,
  push: 0x3a7bd5,
  hit: 0xe8443c,
  throw: 0xd94fd0,
  hitActive: 0xff8a3c,
};

export class DebugOverlay extends System {
  static id = 'dev-overlay';
  static deps = ['combat-sim'];
  static budgetMs = 0.0;

  constructor() {
    super();
    this.bag = new DisposalBag('dev-overlay');
    this.group = null;
    this.enabled = false;
    this.showTelemetry = true;
    /** @type {Array<object>} pooled capsule visualisers */
    this._slots = [];
    this._used = 0;
    this.panel = null;
    this._rows = new Map();
    this._physics = null;
    this._combat = null;
    this._input = null;
    /** Recent events, bounded, for the event feed. */
    this._feed = [];
    this._unsubs = [];
  }

  async init(ctx) {
    // The diagnostic panel is a developer tool, not part of the player-facing composition.
    // Deterministic captures use dev services internally but should still capture the game.
    this.showTelemetry = !!ctx.flags.dev && !ctx.flags.capture;
    this._physics = ctx.systems.get('physics');
    this._combat = ctx.systems.get('combat-sim');
    this._input = ctx.systems.get('core-input');

    this.group = new THREE.Group();
    this.group.name = 'dev-overlay';
    this.group.visible = false;
    if (ctx.render.scene) ctx.render.scene.add(this.group);
    this.bag.custom(() => {
      if (ctx.render.scene) ctx.render.scene.remove(this.group);
    });

    // --- pooled capsule visualisers: cylinder body + two end caps ---
    const cylGeo = new THREE.CylinderGeometry(1, 1, 1, 10, 1, true);
    const sphGeo = new THREE.SphereGeometry(1, 10, 8);
    this.bag.geometry(cylGeo);
    this.bag.geometry(sphGeo);

    for (let i = 0; i < VOLUME_SLOTS; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        wireframe: true,
        transparent: true,
        opacity: 0.55,
        depthTest: false,
      });
      this.bag.material(mat);
      const g = new THREE.Group();
      const body = new THREE.Mesh(cylGeo, mat);
      const capA = new THREE.Mesh(sphGeo, mat);
      const capB = new THREE.Mesh(sphGeo, mat);
      g.add(body, capA, capB);
      g.visible = false;
      g.renderOrder = 999;
      this.group.add(g);
      this._slots.push({ group: g, body, capA, capB, mat });
    }

    this._buildPanel(ctx);
    if (this.panel) this.panel.style.display = this.showTelemetry ? '' : 'none';

    // Toggle keys are dev-only conveniences and deliberately not rebindable.
    if (typeof window !== 'undefined') {
      this.bag.listener(window, 'keydown', (e) => {
        if (e.code === 'F1') {
          this.setEnabled(!this.enabled, ctx);
          e.preventDefault();
        } else if (e.code === 'F2') {
          this.showTelemetry = !this.showTelemetry;
          if (this.panel) this.panel.style.display = this.showTelemetry ? '' : 'none';
          e.preventDefault();
        }
      });
    }

    for (const name of ['combat:hit', 'combat:block', 'combat:counter', 'combat:throw',
      'combat:wall-splat', 'combat:launch', 'combat:parry', 'combat:armor', 'combat:knockdown',
      'combat:round-end']) {
      this._unsubs.push(ctx.events.on(name, (p) => this._note(name, p)));
    }

    // Honour the persisted setting so a capture can request hitboxes without a keypress.
    this.setEnabled(ctx.config.get('showHitboxes'), ctx);
    this._unsubs.push(ctx.config.onChange('showHitboxes', (v) => this.setEnabled(v, ctx)));
  }

  setEnabled(on, ctx) {
    this.enabled = !!on;
    if (this.group) this.group.visible = this.enabled;
    if (!this.enabled) {
      for (const s of this._slots) s.group.visible = false;
    }
    void ctx;
  }

  _note(name, p) {
    if (this._feed.length >= 12) this._feed.shift();
    let detail = '';
    if (p.damage !== undefined && p.damage) detail = `${p.damage}`;
    if (p.comboHits) detail += ` x${p.comboHits}`;
    this._feed.push(`${p.tick} ${name.replace('combat:', '')} ${detail}`.trim());
  }

  // -------------------------------------------------------------------------

  /**
   * Volume transforms are written in `update`, NOT `lateUpdate`.
   *
   * The engine runs every system's `update` before any system's `lateUpdate`, and `render` draws
   * in `lateUpdate`. Positioning the volumes here therefore guarantees they are in place for the
   * draw regardless of registration order. Doing it in `lateUpdate` made the overlay one frame
   * late — and completely invisible in a single-frame capture, which is exactly where it matters.
   */
  update(dt, ctx) {
    if (!this.enabled) return;
    this._used = 0;

    for (let i = 0; i < 2; i++) {
      const vols = this._physics.volumesFor(i);
      if (!vols) continue;
      for (let h = 0; h < HURTBOX_COUNT; h++) this._draw(vols.hurt[h], COLOURS.hurt, 0.35);
      this._draw(vols.push, COLOURS.push, 0.22);
      const f = ctx.sim.fighters[i];
      const active = f.move && f.moveTick >= f.move.tStartup && f.moveTick < f.move.tStartup + f.move.tActive;
      for (let b = 0; b < vols.hitCount; b++) {
        this._draw(vols.hit[b], active ? COLOURS.hitActive : COLOURS.hit, 0.75);
      }
      if (vols.throwActive) this._draw(vols.throwBox, COLOURS.throw, 0.6);
    }

    for (let i = this._used; i < this._slots.length; i++) this._slots[i].group.visible = false;
    void dt;
  }

  /**
   * The telemetry panel updates in `lateUpdate`, after `render` has drawn and written its
   * draw-call and triangle counters for this frame. Reading them in `update` showed 0 on the
   * first presented frame, which made a capture look like nothing had been drawn at all.
   */
  lateUpdate(dt, ctx) {
    if (this.panel && this.showTelemetry) this._updatePanel(ctx);
    void dt;
  }

  /** Positions one pooled visualiser on a capsule. Allocation free. */
  _draw(capsule, colour, opacity) {
    if (!capsule || !capsule.active || this._used >= this._slots.length) return;
    const slot = this._slots[this._used++];
    slot.mat.color.setHex(colour);
    slot.mat.opacity = opacity;
    slot.group.visible = true;

    const ax = capsule.ax;
    const ay = capsule.ay;
    const az = capsule.az;
    const dx = capsule.bx - ax;
    const dy = capsule.by - ay;
    const dz = capsule.bz - az;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const r = capsule.r;

    slot.capA.position.set(ax, ay, az);
    slot.capA.scale.setScalar(r);
    slot.capB.position.set(capsule.bx, capsule.by, capsule.bz);
    slot.capB.scale.setScalar(r);

    if (len < 1e-5) {
      slot.body.visible = false;
      return;
    }
    slot.body.visible = true;
    slot.body.position.set(ax + dx * 0.5, ay + dy * 0.5, az + dz * 0.5);
    slot.body.scale.set(r, len, r);
    // Orient the unit cylinder (Y up) along the segment.
    _up.set(0, 1, 0);
    _dir.set(dx / len, dy / len, dz / len);
    slot.body.quaternion.setFromUnitVectors(_up, _dir);
  }

  // -------------------------------------------------------------------------

  _buildPanel(ctx) {
    if (typeof document === 'undefined') return;
    const root = document.getElementById('dev-root');
    if (!root) return;
    const panel = document.createElement('div');
    panel.className = 'dev-panel';
    root.appendChild(panel);
    this.panel = panel;
    this.bag.custom(() => panel.remove());

    const section = (title) => {
      const h = document.createElement('div');
      h.className = 'dev-section';
      h.textContent = title;
      panel.appendChild(h);
    };
    const row = (key) => {
      const el = document.createElement('div');
      el.className = 'dev-row';
      const k = document.createElement('span');
      k.className = 'dev-key';
      k.textContent = key;
      const v = document.createElement('span');
      v.className = 'dev-val';
      el.append(k, v);
      panel.appendChild(el);
      this._rows.set(key, v);
    };

    section('CLAUDE OF IRON — dev  [F1 boxes] [F2 panel]');
    for (const k of ['fps', 'frame ms', 'tick', 'draw calls', 'tris', 'programs', 'dropped']) row(k);
    section('P1');
    for (const k of ['p1 state', 'p1 move', 'p1 frame', 'p1 pos', 'p1 hp', 'p1 meter', 'p1 combo', 'p1 input']) row(k);
    section('P2');
    for (const k of ['p2 state', 'p2 move', 'p2 frame', 'p2 pos', 'p2 hp', 'p2 meter', 'p2 combo', 'p2 input']) row(k);
    section('MATCH');
    for (const k of ['phase', 'round', 'timer', 'gap', 'advantage', 'hitstop', 'storm']) row(k);
    section('EVENTS');
    const feed = document.createElement('pre');
    feed.className = 'dev-feed';
    panel.appendChild(feed);
    this._feedEl = feed;
    void ctx;
  }

  _set(key, value) {
    const el = this._rows.get(key);
    if (el && el.textContent !== value) el.textContent = value;
  }

  _updatePanel(ctx) {
    const m = ctx.metrics;
    this._set('fps', m.fpsPercentile(0.5).toFixed(0));
    this._set('frame ms', m.frameChannel.last.toFixed(2));
    this._set('tick', String(ctx.time.tick));
    this._set('draw calls', String(m.getCounter('render.drawCalls')));
    this._set('tris', String(m.getCounter('render.triangles')));
    this._set('programs', String(m.getCounter('render.programs')));
    this._set('dropped', String(ctx.time.droppedSteps));

    for (let i = 0; i < 2; i++) {
      const f = ctx.sim.fighters[i];
      if (!f) continue;
      const p = `p${i + 1}`;
      this._set(`${p} state`, `${f.state}${f.frozenTicks > 0 ? ' [frozen]' : ''}`);
      this._set(`${p} move`, f.move ? `${f.move.name} (${f.move.id})` : '—');
      if (f.move) {
        const total = f.move.totalFrames;
        const cur = toF60(f.moveTick).toFixed(0);
        const phase = f.moveTick < f.move.tStartup ? 'startup'
          : f.moveTick < f.move.tStartup + f.move.tActive ? 'ACTIVE' : 'recovery';
        this._set(`${p} frame`, `${cur}/${total} ${phase}  i${f.move.startup} a${f.move.active} r${f.move.recovery}`);
      } else {
        this._set(`${p} frame`, '—');
      }
      this._set(`${p} pos`,
        `${f.x.toFixed(2)},${f.y.toFixed(2)},${f.z.toFixed(2)}  v ${f.vx.toFixed(2)},${f.vy.toFixed(2)}  face ${f.facing > 0 ? '+' : '-'}`);
      this._set(`${p} hp`, `${Math.ceil(f.health)}/${f.maxHealth}  guard ${Math.round((f.guardMeter / f.guardMax) * 100)}%`);
      this._set(`${p} meter`, `${f.stats.meterName} ${Math.floor(f.meter)}/${f.meterMax}${f.stance ? `  [${f.stance}]` : ''}`);
      this._set(`${p} combo`,
        f.comboHits > 0 ? `${f.comboHits} hits, ${f.comboDamage} dmg, scale ${f.comboScale.toFixed(2)}` : '—');
      const snap = ctx.input.players[i];
      this._set(`${p} input`, snap
        ? `${snap.dir} ${snap.command || ''} ${snap.lp ? '1' : ''}${snap.hp ? '2' : ''}${snap.lk ? '3' : ''}${snap.hk ? '4' : ''}${snap.sp ? '5' : ''}${snap.guard ? 'G' : ''} [${snap.device}]`
        : '—');
    }

    const match = ctx.sim.match;
    if (match) {
      this._set('phase', match.phase);
      this._set('round', `${match.round} of ${match.bestOf}  (${match.rounds[0]}-${match.rounds[1]})`);
      this._set('timer', `${Math.ceil(match.timerTicks / 120)}s`);
      this._set('storm', match.storm.toFixed(2));
    }
    this._set('gap', `${this._physics.gap(ctx).toFixed(3)} m`);
    const adv = this._combat.frameAdvantage(ctx, 0);
    this._set('advantage', `P1 ${adv >= 0 ? '+' : ''}${adv}f`);
    this._set('hitstop', String(ctx.sim.hitstop));

    if (this._feedEl) {
      const text = this._feed.join('\n');
      if (this._feedEl.textContent !== text) this._feedEl.textContent = text;
    }
  }

  /** Structured telemetry for tools. */
  snapshot(ctx) {
    return {
      tick: ctx.time.tick,
      metrics: ctx.metrics.snapshot(),
      pools: ctx.pools.stats(),
      camera: this._combat ? null : null,
      volumesDrawn: this._used,
      feed: this._feed.slice(),
    };
  }

  dispose() {
    for (const off of this._unsubs) off();
    this._unsubs.length = 0;
    this._rows.clear();
    this._slots.length = 0;
    this.bag.disposeAll();
  }
}

const _up = new THREE.Vector3(0, 1, 0);
const _dir = new THREE.Vector3();
export { clamp };
